import { useState, type FormEvent } from 'react';
import { looksLikeQty } from '../count/guards';
import { errMsg } from '../lib/useApi';
import { Alert, Button, Input, Modal } from './ui';

const QUICK = ['คีย์จำนวนผิด', 'เลือกหน่วยผิด', 'สแกนผิดสินค้า', 'นับซ้ำ'];

/**
 * Ask for the (mandatory) reason before voiding an entry. A reason that is only a number is usually
 * the new quantity typed in the wrong place: warn once, and offer "แก้ยอด" when the caller has it.
 */
export function VoidDialog({
  open,
  title,
  summary,
  confirmLabel = 'ยืนยันยกเลิกรายการ',
  placeholder = 'เหตุผล (บังคับ)',
  hint,
  onSwitchToFix,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  summary?: string;
  confirmLabel?: string;
  placeholder?: string;
  /** shown under the reason field */
  hint?: string;
  /** offered when the reason looks like a quantity */
  onSwitchToFix?: () => void;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
}) {
  return (
    <Modal open={open} title={title} onClose={onClose}>
      {/* keyed by title: switching void -> fix starts with an empty form */}
      <VoidForm key={title} summary={summary} confirmLabel={confirmLabel} placeholder={placeholder} hint={hint} onSwitchToFix={onSwitchToFix} onConfirm={onConfirm} />
    </Modal>
  );
}

function VoidForm({
  summary,
  confirmLabel,
  placeholder,
  hint,
  onSwitchToFix,
  onConfirm,
}: {
  summary?: string;
  confirmLabel: string;
  placeholder: string;
  hint?: string;
  onSwitchToFix?: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** the numeric reason the user was already warned about — confirming it again goes ahead */
  const [warnedFor, setWarnedFor] = useState<string | null>(null);
  const numericWarning = warnedFor !== null && warnedFor === reason.trim();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 3) return setError('กรุณาระบุเหตุผล (อย่างน้อย 3 ตัวอักษร)');
    if (looksLikeQty(reason) && warnedFor !== reason.trim()) {
      setError('');
      return setWarnedFor(reason.trim());
    }
    setBusy(true);
    setError('');
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {summary && <p className="rounded bg-chip px-3 py-2 text-sm">{summary}</p>}
      {error && <Alert>{error}</Alert>}
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={placeholder} maxLength={500} />
      {hint && <p className="text-xs text-muted">{hint}</p>}
      {numericWarning && (
        <Alert tone="warn">
          <p className="font-medium">ดูเหมือนใส่ยอด — ต้องการ แก้ยอด แทนหรือไม่?</p>
          <p className="mt-1 text-xs">ช่องนี้ใช้ใส่เหตุผล ถ้ากดยืนยันอีกครั้ง รายการจะถูกยกเลิกด้วยเหตุผล "{warnedFor}"</p>
          {onSwitchToFix && (
            <Button type="button" size="sm" variant="primary" className="mt-2" onClick={onSwitchToFix}>
              ไปแก้ยอด
            </Button>
          )}
        </Alert>
      )}
      <div className="flex flex-wrap gap-1.5">
        {QUICK.map((q) => (
          <Button key={q} type="button" size="sm" variant="ghost" className="border-line" onClick={() => setReason(q)}>
            {q}
          </Button>
        ))}
      </div>
      <p className="text-xs text-muted">รายการเดิมไม่ถูกลบ — ถูกทำเครื่องหมายยกเลิกพร้อมเหตุผลและผู้ทำ</p>
      <Button type="submit" variant="danger" className="w-full" loading={busy}>
        {confirmLabel}
      </Button>
    </form>
  );
}
